import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  DetailsRow,
  FontIcon,
  IconButton,
  IDragDropEvents,
  Link,
  mergeStyles,
  mergeStyleSets,
  ShimmeredDetailsListProps,
  Text,
  useFieldArray,
  useTheme
} from "@bps/fluent-ui";
import { dangerButtonStyles } from "@components/buttons/DangerButton";
import { DataTable, DataTableColumn } from "@components/tables/DataTable";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { AssignDeploymentRingFormValues } from "../bulk-actions/formTypes";
import { AssignTenantDeploymentRing } from "./DeploymentRingAssignmentList";

interface DeploymentRingAssignmentTableProps extends ShimmeredDetailsListProps {
  items: AssignTenantDeploymentRing[];
  fieldName: string;
  onRemoveItem: (deploymentRing: AssignTenantDeploymentRing) => void;
}

export const DeploymentRingAssignmentTable: React.FC<DeploymentRingAssignmentTableProps> = ({
  onRemoveItem,
  fieldName,
  ...props
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { actions } = useFieldArray<AssignDeploymentRingFormValues>(fieldName);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { data: hasDeployPermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementDeploy
  );

  const draggedItem = useRef<AssignTenantDeploymentRing | undefined>(undefined);

  const draggedIndex = useRef<number>(-1);
  const dragEnterClass = mergeStyles({
    // override both the background and the color otherwise the default styles are applied
    background: theme.palette.neutralLight,
    backgroundColor: theme.palette.neutralLight
  });

  const insertBeforeItem = async (item: AssignTenantDeploymentRing) => {
    setIsSubmitting(true);

    const draggedItems = [draggedItem.current!];

    const insertIndex = props.items.findIndex(i => i.id === item.id);
    const updatedItems = props.items.filter(
      itm => draggedItems.map(x => x.id).indexOf(itm.id) === -1
    );

    updatedItems.splice(insertIndex, 0, ...draggedItems);

    const updatePromises = Array.from(updatedItems).map(
      async (updatedItem, index) => {
        const originalItem = props.items.find(x => x.id === updatedItem.id);

        if (originalItem) {
          actions.update(index, {
            id: updatedItem.id,
            sortOrder: index + 1
          });
        }
      }
    );

    await Promise.all(updatePromises);

    setIsSubmitting(false);
  };

  const getDragDropEvents = (): IDragDropEvents => {
    return {
      canDrop: () => !isSubmitting,
      canDrag: () => !isSubmitting,
      onDragEnter: () => dragEnterClass,
      onDragLeave: () => {},
      onDrop: async (draggedItem?: any) => {
        if (draggedItem) {
          await insertBeforeItem(draggedItem);
        }
      },
      onDragStart: (item?: any, itemIndex?: number) => {
        draggedItem.current = item;
        draggedIndex.current = itemIndex!;
      },
      onDragEnd: () => {
        if (isSubmitting) return;
        draggedItem.current = undefined;
        draggedIndex.current = -1;
      }
    };
  };

  const columns: DataTableColumn<AssignTenantDeploymentRing>[] = [
    {
      key: "dragDropIcon",
      name: "",
      minWidth: 40,
      maxWidth: 40,
      onRender: () => <FontIcon iconName="DragObject" />
    },
    {
      key: "sortOrder",
      name: "Order",
      minWidth: 100,
      maxWidth: 100,
      isSorted: true,
      isSortedDescending: true,
      onRender: (deploymentRing: AssignTenantDeploymentRing) => (
        <Text>{deploymentRing.sortOrder}</Text>
      )
    },
    {
      key: "name",
      name: "Deployment Ring Name",
      minWidth: 150,
      maxWidth: 220,
      onRender: (deploymentRing: AssignTenantDeploymentRing) => (
        <Link
          onClick={() =>
            navigate(`/system/plt/deployment-rings/${deploymentRing.id}`)
          }
        >
          {deploymentRing.displayName}
        </Link>
      )
    },
    {
      key: "code",
      name: "Deployment Ring Code",
      minWidth: 150,
      maxWidth: 220,
      onRender: (deploymentRing: AssignTenantDeploymentRing) => (
        <Text>{deploymentRing.code}</Text>
      )
    },
    {
      key: "id",
      name: "",
      minWidth: 32,
      onRender: (deploymentRing: AssignTenantDeploymentRing) => (
        <IconButton
          disabled={!hasDeployPermission || isSubmitting}
          title={!hasDeployPermission ? NO_ACTION_PERMISSION : undefined}
          onClick={() => onRemoveItem(deploymentRing)}
          iconProps={{ iconName: "Cancel" }}
          styles={mergeStyleSets(dangerButtonStyles(theme), {
            root: {
              background: "transparent"
            }
          })}
        />
      ),
      sort: false
    }
  ];

  return (
    <DataTable
      stickyHeader
      shimmerLines={5}
      columns={columns}
      onShouldVirtualize={() => true}
      dragDropEvents={getDragDropEvents()}
      onRenderRow={row => (row ? <DetailsRow {...row} /> : null)}
      {...props}
    />
  );
};
